import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { ConfigureService } from '@app/configure/services/configure.service';
import { TrackingService } from '@app/core/services/tracking.service';
import { SessionService } from '@app/core/services/session.service';
import { HelperService } from '@app/core/services/helper.service';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';

import { PartExchangeVehicle } from '@app/configure/models/part-exchange-vehicle.model';
import { FormInputEvent } from '@app/ui-forms/models/form-input-event.model';
import { Dealer } from '@app/core/models/dealer.model';

import { FormHelper } from '@app/ui-forms/classes/form-helper';

import { UISeparatedPipe } from '@app/ui/pipes/separated/separated.pipe';

@Component({
	selector: 'cng-part-exchange-vehicle',
	templateUrl: './part-exchange-vehicle.component.html',
	styleUrls: ['./part-exchange-vehicle.component.less'],
})
export class ConfigurePartExchangeVehicleComponent implements OnInit, OnDestroy {
	@Input() pex_vehicle: PartExchangeVehicle;
	@Output() remove = new EventEmitter();
	@Output() edit = new EventEmitter();
	@Output() show_valuation = new EventEmitter();

	enquiry_hash = this.configure_service.configure_data.meta.enquiry_hash;
	step_mode: string = this.configure_service.getStepMode();
	reserve_available: boolean = this.configure_service.isStepDealerEnabled('reserve');
	is_default_vehicle: boolean = this.configure_service.configure_data.steps.purchase_vehicle.data.is_default_vehicle;
	pex_vehicle_info: any = null;
	pex_lookup_method: 'registration' | 'manual' = 'registration';
	dealer: Dealer = this.session_service.getDealer();
	website_url: string = this.dealer.website_url || null;

	//  LOOKUP FORM
	pex_lookup_form: FormGroup;
	pex_lookup_form_submitting: boolean = false;
	type_label: string = 'Loading...';
	manufacturer_label: string = 'Loading...';
	manual_lookup_types: Array<any> = [];
	manual_lookup_manufacturers: Array<any> = [];
	manual_lookup_ranges: Array<any> = [];
	manual_lookup_years: Array<any> = [];
	manual_lookup_models: Array<any> = [];
	manual_lookup_filtered_models: Array<any> = [];
	manual_lookup_editions: Array<any> = [];

	//  DETAILS FORM
	pex_details_form: FormGroup;
	editions: Array<any>;
	conditions: Array<any> = [
		{ value: '' },
		{ value: 'clean', name: 'Above Average' },
		{ value: 'average', name: 'Average' },
		{ value: 'below', name: 'Below Average' },
	];
	pex_details_form_submitting: boolean = false;
	asking_price: boolean = this.configure_service.configure_data.meta.pex_asking_price;
	instant_valuation: boolean = this.configure_service.configure_data.meta.pex_instant_valuation;
	finance_locked: boolean = this.configure_service.configure_data.meta.finance.locked;
	lte_xs: boolean = false;
	tracking_log: string[] = [];
	destroyed$: Subject<boolean> = new Subject<boolean>();

	constructor(
		private configure_service: ConfigureService,
		private toast_service: ToastService,
		private element_ref: ElementRef,
		private separated_pipe: UISeparatedPipe,
		private modal_service: ModalService,
		private tracking_service: TrackingService,
		private helper_service: HelperService,
		private session_service: SessionService
	) {}

	ngOnInit(): void {
		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data) => {
			this.asking_price = configure_data.meta.pex_asking_price;
			this.instant_valuation = configure_data.meta.pex_instant_valuation;
			this.finance_locked = configure_data.meta.finance.locked;
		});

		this.buildForms();
		this.checkState();
		this.checkScreenWidth();
	}

	buildForms(): void {
		this.pex_lookup_form = new FormGroup({
			registration: new FormControl('', [
				Validators.required,
				Validators.maxLength(8),
				Validators.pattern('^[ a-zA-Z0-9]*$'),
			]),
		});

		this.pex_details_form = new FormGroup({
			edition: new FormControl('', Validators.required),
			mileage: new FormControl('', [Validators.required, Validators.min(0), Validators.max(999999)]),
			condition: new FormControl('', Validators.required),
			finance_settlement: new FormControl('', [Validators.min(0), Validators.max(999999)]),
			notes: new FormControl('', Validators.maxLength(1000)),
			// 'terms_and_conditions': new FormControl(false, Validators.requiredTrue)
		});

		if (this.asking_price) {
			this.pex_details_form.addControl(
				'asking_price',
				new FormControl('', [Validators.required, Validators.min(0), Validators.max(5000000)])
			);
		}
	}

	checkState(): void {
		if (this.pex_vehicle.vehicle !== null) {
			this.pex_vehicle.state = 'c';

			this.pex_lookup_form.controls['registration'].setValue(this.pex_vehicle.registration);

			this.setRegistrationLookupEditions(this.pex_vehicle.cap_codes);
			this.pex_details_form.controls['edition'].setValue(this.dealer.use_vehicle_lookup_service ? this.pex_vehicle.vehicle.derivative_id : this.pex_vehicle.vehicle.cap_id);
			this.pex_details_form.controls['mileage'].setValue(this.pex_vehicle.vehicle.mileage);
			this.pex_details_form.controls['condition'].setValue(this.pex_vehicle.vehicle.condition);
			this.pex_details_form.controls['finance_settlement'].setValue(this.pex_vehicle.vehicle.finance_settlement);
			this.pex_details_form.controls['notes'].setValue(this.pex_vehicle.vehicle.notes);
			// this.pex_details_form.controls['terms_and_conditions'].setValue(false);

			if (this.asking_price) {
				this.pex_details_form.controls['asking_price'].setValue(this.pex_vehicle.vehicle.asking_price);
			}

			this.pex_vehicle_info = {
				make: this.pex_vehicle.vehicle.make,
				model: this.pex_vehicle.vehicle.model,
				edition: this.pex_vehicle.vehicle.edition,
				registration: this.pex_vehicle.registration,
			};
		} else if (this.pex_vehicle.cap_codes !== null && this.pex_vehicle.cap_codes.length > 0) {
			this.pex_vehicle.state = 'b';

			this.pex_lookup_form.controls['registration'].setValue(this.pex_vehicle.registration);
			this.setRegistrationLookupEditions(this.pex_vehicle.cap_codes);

			this.pex_vehicle_info = {
				make: this.pex_vehicle.cap_codes[0].make,
				model: this.pex_vehicle.cap_codes[0].model,
				edition: this.pex_vehicle.cap_codes.length === 1 ? this.pex_vehicle.cap_codes[0].edition : null,
				registration: this.pex_vehicle.registration,
			};
		} else {
			this.pex_vehicle.state = 'a';
			setTimeout(() => {
				this.element_ref.nativeElement.querySelectorAll('.state-a input[name="registration"]')[0].focus();
			});
		}

		const force_state = this.pex_vehicle.force_state;

		if (force_state !== null && typeof force_state !== 'undefined') {
			if (this.pex_vehicle.force_state === 'r') {
				setTimeout(() => {
					this.onRemove();
				});
			} else {
				this.pex_vehicle.state = this.pex_vehicle.force_state;
			}

			this.pex_vehicle.force_state = null;
		}
	}

	onSubmitLookup(): void {
		// if the PEX details form has already been submitted and is still loading data
		if (this.pex_lookup_form_submitting) {
			return;
		}
		this.tracking_service.trackFormSubmitAttempt(
			'part_exchange_lookup_form',
			FormHelper.getCompletedControls(this.pex_lookup_form),
			FormHelper.getEmptyControls(this.pex_lookup_form)
		);

		if (this.pex_lookup_form.invalid) {
			FormHelper.forceValidate(this.pex_lookup_form);
			this.tracking_service.trackFormValidationFailure(
				'part_exchange_lookup_form',
				'invalid_fields[' + FormHelper.getInvalidControls(this.pex_lookup_form).join(',') + ']'
			);
			this.toast_service.error('Form has invalid fields');
		} else {
			this.tracking_service.trackFormValidationPass('part_exchange_lookup_form');
			this.pex_lookup_form_submitting = true;

			const body = {
				registration: this.pex_lookup_form.controls['registration'].value.replace(/ /g, ''),
				cap_id: this.dealer.use_vehicle_lookup_service === false && this.pex_lookup_method === 'manual' ? this.pex_lookup_form.controls['edition'].value : null,
				derivative_id: this.dealer.use_vehicle_lookup_service === true && this.pex_lookup_method === 'manual' ? this.pex_lookup_form.controls['edition'].value : null,
				year: this.pex_lookup_method === 'manual' ? this.pex_lookup_form.controls['year'].value : null,
			};

			this.configure_service.createPartExVehicle(body).subscribe((response: any) => {
				if (response !== null) {
					this.helper_service.scrollTop();
					this.tracking_service.trackFormSubmitSuccess('part_exchange_lookup_form');
				} else {
					if (this.pex_lookup_method === 'registration') {
						this.tracking_service.trackFormSubmitFailure(
							'part_exchange_lookup_form',
							'registration_not_found'
						);
						this.pex_lookup_method = 'manual';
						this.toast_service.add({
							message: 'Registration not found, please try manual lookup',
							type: 'info',
							icon: 'icon-info',
							duration: 5000,
						});

						if (this.dealer.use_vehicle_lookup_service) {
							this.pex_lookup_form.addControl('type', new FormControl('', Validators.required));
							this.getTypes();
							this.manufacturer_label = 'Make';
						} else {
							this.getManufacturers();
						}

						this.pex_lookup_form.addControl('manufacturer', new FormControl('', Validators.required));
						this.pex_lookup_form.addControl('range', new FormControl('', Validators.required));
						this.pex_lookup_form.addControl('year', new FormControl('', Validators.required));
						this.pex_lookup_form.addControl('model', new FormControl('', Validators.required));
						this.pex_lookup_form.addControl('edition', new FormControl('', Validators.required));
					} else {
						this.tracking_service.trackFormSubmitFailure(
							'part_exchange_lookup_form',
							'manual_lookup_failed'
						);
						this.configure_service.setSoftError({
							title: 'Vehicle lookup failed',
							message:
								"We're sorry, we weren't able to find that vehicle, please arrange a callback so that we are able to discuss your part exchange details.",
						});
					}
				}
				this.pex_lookup_form_submitting = false;
			});
		}
	}

	setRegistrationLookupEditions(cap_codes: any): void {
		this.editions = cap_codes.map((item) => {
			return {
				value: this.dealer.use_vehicle_lookup_service ? item.derivative_id : item.cap_id,
				name: item.edition,
			};
		});

		if (this.editions.length === 1) {
			this.pex_details_form.controls['edition'].setValue(this.editions[0].value);
		} else {
			this.editions.splice(0, 0, { value: '' });
		}
	}

	getTypes(): void {
		this.configure_service.getVehicleTypes().subscribe((types) => {
			if (types) {
				this.type_label = 'Type';
				this.manual_lookup_types = types.map((type) => {
					return {
						value: type.type_code,
						name: type.type_name,
					};
				});
				this.manual_lookup_types.splice(0, 0, { value: '' });
				this.pex_lookup_form.controls['type'].setValue('');
			}
		});
	}

	getManufacturers(type = null): void {
		this.configure_service.getVehicleManufacturers(type).subscribe((manufacturers) => {
			if (manufacturers) {
				this.manufacturer_label = 'Make';
				this.manual_lookup_manufacturers = manufacturers.map((manufacturer) => {
					return {
						value: manufacturer.manufacturer_code,
						name: manufacturer.manufacturer_name,
					};
				});
				this.manual_lookup_manufacturers.splice(0, 0, { value: '' });
				this.pex_lookup_form.controls['manufacturer'].setValue('');
			}
		});
	}

	getRanges(manufacturer_id, type = null): void {
		this.configure_service.getVehicleRanges(manufacturer_id, type).subscribe((ranges) => {
			if (ranges) {
				this.manual_lookup_ranges = ranges.map((range) => {
					return {
						value: range.range_code + '|' + range.min_year + '|' + range.max_year,
						name: range.range_name,
					};
				});
				this.manual_lookup_ranges.splice(0, 0, { value: '' });
				this.pex_lookup_form.controls['range'].setValue('');
			}
		});
	}

	getYears(manufacturer_id, range_id): void {
		this.configure_service.getVehicleModels(manufacturer_id, range_id).subscribe((models) => {
			if (models) {
				this.manual_lookup_models = models;
				this.manual_lookup_years = [];

				const min = models.reduce((previous, current) => {
					return previous.from_year < current.from_year ? previous : current;
				});

				const max = models.reduce((previous, current) => {
					return previous.to_year > current.to_year ? previous : current;
				});

				const min_year: number = min.from_year;
				const max_year: number =
					max.to_year === null || max.to_year === 0 ? new Date().getFullYear() : max.to_year;

				for (let i = max_year; i >= min_year; i--) {
					this.manual_lookup_years.push({ name: i, value: i });
				}

				this.manual_lookup_years.splice(0, 0, { value: '' });
				this.pex_lookup_form.controls['year'].setValue('');
			}
		});
	}

	getModels(year): void {
		this.manual_lookup_filtered_models = this.manual_lookup_models
			.filter((model) => {
				if (model.to_year === null && model.from_year <= year) {
					return model;
				} else if (model.to_year !== null && model.from_year <= year && model.to_year >= year) {
					return model;
				}
			})
			.map((model) => {
				return {
					value: model.model_code,
					name:
						model.model_name +
						' (' +
						model.from_year +
						(model.to_year === null ? ' onwards' : ' - ' + model.to_year) +
						')',
				};
			});

		this.manual_lookup_filtered_models.splice(0, 0, { value: '' });
		this.pex_lookup_form.controls['model'].setValue('');
	}

	getEditions(manufacturer_id, range_id, year, model_id): void {
		this.configure_service.getVehicleEditions(manufacturer_id, range_id, model_id).subscribe((editions) => {
			if (editions) {
				this.manual_lookup_editions = editions
					.filter((edition) => {
						if (edition.to_year === null && edition.from_year <= year) {
							return edition;
						} else if (edition.to_year !== null && edition.from_year <= year && edition.to_year >= year) {
							return edition;
						}
					})
					.map((edition) => {
						return {
							value: edition.edition_id,
							name:
								edition.edition_name +
								' (' +
								edition.from_year +
								(edition.to_year === null ? ' onwards' : ' - ' + edition.to_year) +
								')',
						};
					});
				this.manual_lookup_editions.splice(0, 0, { value: '' });
				this.pex_lookup_form.controls['edition'].setValue('');
			}
		});
	}

	resetManufacturers(): void {
		this.manual_lookup_manufacturers = [{ value: '' }];
		this.pex_lookup_form.controls['manufacturer'].setValue('');
		this.pex_lookup_form.controls['manufacturer'].markAsUntouched();
		this.pex_lookup_form.controls['manufacturer'].markAsPristine();
	}

	resetRanges(): void {
		this.manual_lookup_ranges = [{ value: '' }];
		this.pex_lookup_form.controls['range'].setValue('');
		this.pex_lookup_form.controls['range'].markAsUntouched();
		this.pex_lookup_form.controls['range'].markAsPristine();
	}

	resetYears(): void {
		this.manual_lookup_years = [{ value: '' }];
		this.pex_lookup_form.controls['year'].setValue('');
		this.pex_lookup_form.controls['year'].markAsUntouched();
		this.pex_lookup_form.controls['year'].markAsPristine();
	}

	resetModels(): void {
		this.manual_lookup_filtered_models = [{ value: '' }];
		this.pex_lookup_form.controls['model'].setValue('');
		this.pex_lookup_form.controls['model'].markAsUntouched();
		this.pex_lookup_form.controls['model'].markAsPristine();
	}

	resetEditions(): void {
		this.manual_lookup_editions = [{ value: '' }];
		this.pex_lookup_form.controls['edition'].setValue('');
		this.pex_lookup_form.controls['edition'].markAsUntouched();
		this.pex_lookup_form.controls['edition'].markAsPristine();
	}

	onTypeChanged(type: any): void {
		this.resetManufacturers();
		this.resetYears();
		this.resetRanges();
		this.resetModels();
		this.resetEditions();

		if (type.value !== '') {
			this.getManufacturers(type.value);
		}
	}

	onManufacturerChanged(manufacturer: any): void {
		this.resetYears();
		this.resetRanges();
		this.resetModels();
		this.resetEditions();

		if (manufacturer.value !== '') {
			this.getRanges(manufacturer.value, this.dealer.use_vehicle_lookup_service ? this.pex_lookup_form.controls['type'].value : null);
		}
	}

	onRangeChanged(range: any): void {
		this.resetYears();
		this.resetModels();
		this.resetEditions();

		if (range.value !== '') {
			this.getYears(
				this.pex_lookup_form.controls['manufacturer'].value,
				this.pex_lookup_form.controls['range'].value.split('|')[0]
			);
		}
	}

	onYearChanged(year: any): void {
		this.resetModels();
		this.resetEditions();
		if (year.value !== '') {
			this.getModels(year.value);
		}
	}

	onModelChanged(model: any): void {
		this.resetEditions();
		if (model.value !== '') {
			this.getEditions(
				this.pex_lookup_form.controls['manufacturer'].value,
				this.pex_lookup_form.controls['range'].value.split('|')[0],
				this.pex_lookup_form.controls['year'].value,
				model.value
			);
		}
	}

	checkMakeModelControlState(control_keys: Array<any>): void {
		for (const control_key of control_keys) {
			if (this.pex_lookup_form.controls[control_key].value === '') {
				this.pex_lookup_form.controls[control_key].markAsDirty();
			}
		}
	}

	//  DETAILS

	onSubmitDetails(): void {
		if (this.finance_locked) {
			this.modal_service.show(['configure/finance-locked']);
			return;
		}

		// if the valuation is still loading
		if (this.pex_details_form_submitting) {
			return;
		}

		this.tracking_service.trackFormSubmitAttempt(
			'part_exchange_details_form',
			FormHelper.getCompletedControls(this.pex_details_form),
			FormHelper.getEmptyControls(this.pex_details_form)
		);

		if (this.pex_details_form.invalid) {
			FormHelper.forceValidate(this.pex_details_form);
			this.tracking_service.trackFormValidationFailure(
				'part_exchange_details_form',
				'invalid_fields[' + FormHelper.getInvalidControls(this.pex_details_form).join(',') + ']'
			);
			this.toast_service.error('Form has invalid fields');
		} else {
			this.tracking_service.trackFormValidationPass('part_exchange_details_form');
			this.pex_details_form_submitting = true;

			const body = {
				cap_id: this.dealer.use_vehicle_lookup_service === false ? this.pex_details_form.controls['edition'].value : null,
				derivative_id: this.dealer.use_vehicle_lookup_service === true ? this.pex_details_form.controls['edition'].value : null,
				mileage: parseInt(this.pex_details_form.controls['mileage'].value, 10),
				condition: this.pex_details_form.controls['condition'].value,
				asking_price: this.asking_price
					? parseFloat(this.pex_details_form.controls['asking_price'].value) || null
					: null,
				finance_settlement: parseFloat(this.pex_details_form.controls['finance_settlement'].value) || null,
				notes:
					this.pex_details_form.controls['notes'].value === ''
						? null
						: this.pex_details_form.controls['notes'].value,
			};

			this.configure_service.updatePartExVehicle(body, false).subscribe((response1) => {
				if (response1) {
					this.configure_service.saveCustomiseReview(true).subscribe((response2) => {
						if (response2) {
							if (
								parseInt(this.pex_details_form.controls['mileage'].value, 10) !==
								response1.vehicle.mileage
							) {
								this.toast_service.info(
									'Mileage adjusted to ' + this.separated_pipe.transform(response1.vehicle.mileage)
								);
							}

							this.tracking_service.trackFormSubmitSuccess('part_exchange_details_form');
							this.tracking_service.trackPartExchangeCompleted();

							this.helper_service.scrollTop();
						} else {
							this.configure_service.setSoftError({
								title: 'Instant Valuation Not Available',
								message:
									'Unfortunately we cannot provide an instant valuation for this vehicle. Our experts will evaluate the information you have provided and make an assessment soon. They may need to contact you for further details. Please let us know when would be the most convenient time to call.',
							});
							this.pex_details_form_submitting = false;
						}
					});
				} else {
					this.configure_service.setSoftError({
						title: 'Instant Valuation Not Available',
						message:
							'Unfortunately we cannot provide an instant valuation for this vehicle. Our experts will evaluate the information you have provided and make an assessment soon. They may need to contact you for further details. Please let us know when would be the most convenient time to call.',
					});
					this.tracking_service.trackFormSubmitFailure(
						'part_exchange_details_form',
						'valuation_not_available'
					);
					this.pex_details_form_submitting = false;
				}
			});
		}
	}

	onRemove(): void {
		this.remove.emit();
		this.helper_service.scrollTop();
	}

	getCondition(value: string): string {
		return this.conditions.find((item) => item.value === value).name;
	}

	getValuationLabel(): string {
		if (!this.asking_price) {
			return 'Valuation Estimate:';
		}

		if (this.pex_vehicle.vehicle.allowance !== this.pex_vehicle.vehicle.asking_price) {
			return 'Valuation:';
		}

		return 'Asking Price:';
	}

	//  FOCUS HELPERS

	onAddAskingPrice(): void {
		this.pex_details_form.controls['asking_price'].reset();
		this.onEdit('.state-b input[name="asking_price"]');
	}

	onAddNotes(): void {
		this.pex_details_form.controls['notes'].reset();
		this.onEdit('.state-b textarea[name="notes"]');
	}

	onAddFinanceSettlement(): void {
		this.pex_details_form.controls['finance_settlement'].reset();
		this.onEdit('.state-b input[name="finance_settlement"]');
	}

	onAddCondition(): void {
		this.pex_details_form.controls['condition'].reset();
		this.onEdit('.state-b select[name="condition"]');
	}

	onEdit(element_to_focus: string = null): void {
		if (this.finance_locked) {
			this.modal_service.show(['configure/finance-locked']);
			return;
		}

		this.pex_vehicle.state = 'b';
		this.edit.emit();
		this.helper_service.scrollTop();
		if (element_to_focus !== null) {
			setTimeout(() => {
				this.element_ref.nativeElement.querySelectorAll(element_to_focus)[0].focus();
			});
		}
	}

	onReserve(): void {
		if (this.step_mode === 'part_exchange' && this.configure_service.isStepDealerEnabled('reserve')) {
			this.configure_service.enableStep('reserve');
			this.configure_service.navigateToStep('reserve');
		}
	}

	@HostListener('window:resize')
	checkScreenWidth(): void {
		this.lte_xs = this.helper_service.isScreenWidth('<=', 'xs');
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}

	get buttonLabel(): string {
		if (!this.instant_valuation) return 'Save Details';
		return 'Get Valuation';
	}

	onFormInput(form: any): void {
		const event: FormInputEvent = FormHelper.getInputEvent(form);
		if (this.tracking_log.indexOf(event.log_name) === -1) {
			this.tracking_service.trackFormInput(event.form_name, event.input_type, event.input_name);
			this.tracking_log.push(event.log_name);
		}
	}
}
